import {
  type CalendarPeriod,
  type CalendarRow,
  canAnywhere,
  DOC_RULE_CONDITIONS,
  DOC_RULE_CONTRACT_KINDS,
  type DocRuleCondition,
  type DocRuleContractKind,
  FUNDING_SOURCES,
  PAYMENT_DOC_KINDS,
  PAYMENT_KINDS,
  PAYMENT_PRIORITIES,
  PAYMENT_REQUEST_STATUSES,
  type PaymentCalendar,
  type PaymentDocRulesResponse,
  type PaymentDocRuleView,
  type PaymentKind,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, tenge, tengeShort, todayLocal } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { AdvancesView } from './Advances';
import { CashflowView } from './Cashflow';
import { Export1cView, LimitsView } from './FinanceControl';
import { MoneyPaymentsView } from './MoneyPayments';
import { PaymentRegistryTab } from './PaymentRegistry';

const FIN_TABS = [
  { key: 'calendar', title: 'Платёжный календарь' },
  { key: 'registry', title: 'Реестр платежей' },
  { key: 'cashflow', title: 'ДДС' },
  { key: 'advances', title: 'Авансы и удержания' },
  { key: 'bank', title: 'Оплаты из банка' },
  { key: 'docs', title: 'Комплекты документов к оплате' },
  { key: 'limits', title: 'Лимиты и сверка' },
  { key: 'export', title: 'Выгрузка в 1С' },
] as const;
type FinTab = (typeof FIN_TABS)[number]['key'];

const plusDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

/** Раздел «Финансы»: платёжный календарь компании и проектов (ФН-02) и справочник комплектов (ФН-19) */
type FinSearch = {
  tab?: string;
  projectId?: string;
  from?: string;
  to?: string;
  by?: 'days';
  registryId?: string;
};

export function FinancePage() {
  const params = useSearch({ strict: false }) as FinSearch;
  const tab: FinTab = FIN_TABS.find((t) => t.key === params.tab)?.key ?? 'calendar';
  const navigate = useNavigate();
  const me = useMe();
  // Раздел видят те, кто смотрит финансы или закупки хотя бы одного проекта
  const allowed = canAnywhere(me.access, 'finance.view') || canAnywhere(me.access, 'procurement.view');
  const pick = (t: FinTab) => void navigate({ to: '/finance', search: t === 'calendar' ? {} : { tab: t } });
  // Календарь проекта — со ссылки из вкладки «Финансы» проекта: проект в строке «где вы находитесь»
  const projects = useQuery({ ...projectsQuery('all'), enabled: allowed && Boolean(params.projectId) });
  const projectCode = projects.data?.find((x) => x.id === params.projectId)?.code;
  useCrumbs(
    !allowed
      ? [{ label: 'Нет доступа' }]
      : tab === 'registry'
        ? params.registryId
          ? [{ label: 'Реестр платежей', to: '/finance?tab=registry' }, { label: 'Реестр' }]
          : [{ label: 'Реестр платежей' }]
        : tab === 'cashflow'
          ? [{ label: 'ДДС' }]
          : tab === 'advances'
            ? [{ label: 'Авансы и удержания' }]
            : tab === 'bank'
              ? [{ label: 'Оплаты из банка' }]
              : tab === 'docs'
                ? [{ label: 'Комплекты документов' }]
                : tab === 'limits'
                  ? [{ label: 'Лимиты и сверка' }]
                  : tab === 'export'
                    ? [{ label: 'Выгрузка в 1С' }]
                    : projectCode
                      ? [{ label: `Календарь ${projectCode}` }]
                      : [],
  );
  if (!allowed)
    return (
      <Page>
        <PageHeader title="Финансы" />
        <EmptyState title="Нет доступа к разделу">
          Раздел «Финансы» открыт сотрудникам, которые смотрят финансы или закупки проекта. Доступ выдаёт
          администратор портала.
        </EmptyState>
      </Page>
    );
  return (
    <Page>
      <PageHeader
        title="Финансы"
        subtitle="Плановые платежи по договорам и заявки на оплату по дням и неделям, лимиты платежей, комплекты документов к оплате"
      />
      <div
        role="tablist"
        aria-label="Финансы"
        className="mb-4 inline-flex flex-wrap rounded-md border border-line bg-surface p-0.5"
      >
        {FIN_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => pick(t.key)}
            className={clsx(
              'min-h-11 rounded-[5px] px-3 text-[13px] font-medium md:min-h-9',
              tab === t.key ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t.title}
          </button>
        ))}
      </div>
      {tab === 'calendar' ? (
        <CalendarView search={params} />
      ) : tab === 'registry' ? (
        <PaymentRegistryTab registryId={params.registryId} />
      ) : tab === 'cashflow' ? (
        <CashflowView projectId={params.projectId} />
      ) : tab === 'advances' ? (
        <AdvancesView projectId={params.projectId} />
      ) : tab === 'bank' ? (
        <MoneyPaymentsView />
      ) : tab === 'docs' ? (
        <DocRulesView />
      ) : tab === 'limits' ? (
        <LimitsView />
      ) : (
        <Export1cView />
      )}
    </Page>
  );
}

/* ───────────────────────── Платёжный календарь ───────────────────────── */

function CalendarView({ search }: { search: FinSearch }) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const today = todayLocal();
  const navigate = useNavigate();
  // Фильтры — в адресе страницы: календарь проекта открывается ссылкой, «назад» возвращает прежний вид
  const f = {
    projectId: search.projectId ?? '',
    from: search.from ?? today,
    to: search.to ?? plusDays(search.from ?? today, 27),
    by: search.by === 'days' ? ('days' as const) : ('weeks' as const),
  };
  const setF = (v: typeof f) =>
    void navigate({
      to: '/finance',
      search: {
        projectId: v.projectId || undefined,
        from: v.from === today ? undefined : v.from,
        to: v.to === plusDays(v.from, 27) ? undefined : v.to,
        by: v.by === 'days' ? 'days' : undefined,
      },
      replace: true,
    });
  const projects = useQuery(projectsQuery('all'));
  const q = useQuery({
    queryKey: ['finance', 'calendar', f.projectId, f.from, f.to],
    queryFn: () =>
      api<PaymentCalendar>('/finance/calendar', {
        query: { from: f.from, to: f.to, projectId: f.projectId || undefined },
      }),
    enabled: money && f.from <= f.to,
  });
  const [limits, setLimits] = useState(false);
  if (!money)
    return (
      <EmptyState title="Платёжный календарь — с суммами">
        Календарь показывают сотрудникам с уровнем денег «Акты и счета» или выше: уровень выдаёт владелец
        портала.
      </EmptyState>
    );
  const c = q.data;
  const periods = c ? (f.by === 'weeks' ? c.weeks : c.days) : [];
  const byDate = new Map<string, CalendarRow[]>();
  for (const r of c?.rows ?? []) {
    const k = r.date ?? '';
    byDate.set(k, [...(byDate.get(k) ?? []), r]);
  }
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]">
        <Field label="Проект">
          <Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            <option value="">Все проекты</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="С">
          <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
        </Field>
        <Field label="По">
          <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
        </Field>
        <Field label="Итоги">
          <Select value={f.by} onChange={(e) => setF({ ...f, by: e.target.value as 'days' | 'weeks' })}>
            <option value="weeks">по неделям</option>
            <option value="days">по дням</option>
          </Select>
        </Field>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : c ? (
        <>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-4">
            {[
              { label: 'План по договорам', value: c.totals.planned },
              { label: 'В заявках на оплату, с черновиками', value: c.totals.requested },
              { label: 'Оплачено', value: c.totals.paid },
              { label: 'Просрочено', value: c.totals.overdue, danger: c.totals.overdue > 0 },
            ].map((x) => (
              <div key={x.label} className="bg-surface px-4 py-3">
                <dt className="text-[12px] text-ink-2">{x.label}</dt>
                <dd className={clsx('num mt-1 text-[18px] font-semibold', x.danger && 'text-danger')}>
                  {tengeShort(x.value)}
                </dd>
              </div>
            ))}
          </dl>

          <Panel
            title={f.by === 'weeks' ? 'По неделям' : 'По дням'}
            meta={
              !c.limitsShown
                ? 'лимиты платежей — на всю компанию: они в календаре всех проектов'
                : c.limits.daily || c.limits.weekly
                  ? `лимит: ${[c.limits.daily ? `день ${tengeShort(c.limits.daily)}` : null, c.limits.weekly ? `неделя ${tengeShort(c.limits.weekly)}` : null].filter(Boolean).join(', ')}`
                  : 'лимиты платежей не заданы'
            }
            actions={
              c.can.limits ? (
                <Button size="sm" variant="secondary" onClick={() => setLimits(true)}>
                  Лимиты платежей
                </Button>
              ) : null
            }
            bodyClassName="p-0"
          >
            {periods.length ? (
              <ul className="divide-y divide-line">
                {periods.map((p) => (
                  <PeriodRow key={p.start} p={p} weeks={f.by === 'weeks'} limits={c.limitsShown} />
                ))}
              </ul>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">За период платежей нет.</p>
            )}
          </Panel>

          <Panel
            title="Платежи"
            meta="плановые платежи из графиков подписанных договоров и заявки на оплату"
            bodyClassName="p-0"
          >
            {c.rows.length ? (
              <div className="divide-y divide-line">
                {[...byDate.entries()].map(([d, rows]) => (
                  <section key={d} className="px-4 py-3 md:px-5">
                    <h3 className="num mb-2 text-[13px] font-semibold text-ink">
                      {date(d)}
                      <span className="ml-2 font-normal text-ink-2">
                        {tenge(rows.reduce((s, r) => s + r.amount, 0))}
                      </span>
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {rows.map((r) => (
                        <CalendarItem key={`${r.kind}:${r.id}`} r={r} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">За период платежей нет.</p>
            )}
          </Panel>

          {c.undated.length ? (
            <Panel
              title="Без даты"
              meta="плановые платежи «по поставке» и «после накладной» без графика поставки и заявки на оплату без плановой даты"
              bodyClassName="p-0"
            >
              <ul className="flex flex-col gap-2 px-4 py-3 md:px-5">
                {c.undated.map((r) => (
                  <CalendarItem key={`${r.kind}:${r.id}`} r={r} />
                ))}
              </ul>
            </Panel>
          ) : null}
          {limits ? <LimitsDialog current={c.limits} onClose={() => setLimits(false)} /> : null}
        </>
      ) : null}
    </div>
  );
}

function PeriodRow({ p, weeks, limits }: { p: CalendarPeriod; weeks: boolean; limits: boolean }) {
  return (
    <li
      className={clsx(
        'grid gap-1 px-4 py-2.5 text-[13px] md:grid-cols-[180px_repeat(4,minmax(0,1fr))] md:px-5',
        p.over && 'bg-danger-soft',
      )}
    >
      <span className="num font-medium text-ink">
        {weeks ? `${date(p.start)} — ${date(plusDays(p.start, 6))}` : date(p.start)}
        {p.over ? <span className="ml-2 text-danger">сверх лимита</span> : null}
      </span>
      <span className="num text-ink-2">
        план <span className="text-ink">{tenge(p.planned)}</span>
      </span>
      <span className="num text-ink-2">
        заявки <span className="text-ink">{tenge(p.requested)}</span>
      </span>
      <span className="num text-ink-2">
        оплачено <span className="text-ink">{tenge(p.paid)}</span>
      </span>
      {/* В календаре проекта лимитов нет: они на всю компанию */}
      {limits ? (
        <span className="num text-ink-2">
          {p.limit !== null ? (
            <>
              лимит <span className={clsx(p.over ? 'text-danger' : 'text-ink')}>{tenge(p.limit)}</span>
            </>
          ) : (
            'без лимита'
          )}
        </span>
      ) : null}
    </li>
  );
}

function CalendarItem({ r }: { r: CalendarRow }) {
  return (
    <li className="flex flex-col gap-1 rounded-md border border-line px-3 py-2 text-[13px] sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {r.kind === 'request' ? (
            <>
              <span className="font-medium text-ink">{r.number}</span>
              {r.status ? (
                <Badge tone={r.status === 'paid' ? 'success' : r.status === 'hold' ? 'danger' : 'info'}>
                  {PAYMENT_REQUEST_STATUSES[r.status]}
                </Badge>
              ) : null}
            </>
          ) : r.kind === 'fact' ? (
            <>
              {r.number ? <span className="font-medium text-ink">{r.number}</span> : null}
              <Badge tone="success">оплата без заявки</Badge>
            </>
          ) : (
            <Badge tone="neutral">план по договору</Badge>
          )}
          {r.priority !== 'normal' ? (
            <Badge tone={r.priority === 'high' ? 'warning' : 'neutral'}>
              {PAYMENT_PRIORITIES[r.priority]}
            </Badge>
          ) : null}
          {r.overdue ? <span className="text-danger">просрочено</span> : null}
          {r.moves ? <span className="text-ink-2">переносов: {r.moves}</span> : null}
        </div>
        <p className="mt-0.5 text-ink [overflow-wrap:anywhere]">
          {r.counterpartyName ? `${r.counterpartyName} — ` : ''}
          {r.description}
        </p>
        <p className="text-[12px] text-ink-2">
          {r.projectCode}
          {r.costArticle ? ` · ${r.costArticle}` : ''}
          {r.fundingSource ? ` · ${FUNDING_SOURCES[r.fundingSource]}` : ''}
          {r.dateNote ? ` · ${r.dateNote}` : ''}
        </p>
        {r.contractId ? (
          // Ссылка отдельной строкой: на телефоне её удобно нажать пальцем
          <Link
            to={`/contracts/${r.contractId}`}
            className="inline-flex min-h-11 items-center text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-0"
          >
            Договор {r.contractNumber}
          </Link>
        ) : null}
      </div>
      <span className="num shrink-0 text-[14px] font-medium text-ink sm:text-right">{tenge(r.amount)}</span>
    </li>
  );
}

function LimitsDialog({
  current,
  onClose,
}: {
  current: { daily: number | null; weekly: number | null };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    daily: current.daily !== null ? String(current.daily) : '',
    weekly: current.weekly !== null ? String(current.weekly) : '',
  });
  const ok = (v: string) => !v.trim() || num(v) > 0;
  const save = useMutation({
    mutationFn: () =>
      api('/finance/payment-limits', {
        method: 'PUT',
        body: {
          daily: f.daily.trim() ? num(f.daily) : null,
          weekly: f.weekly.trim() ? num(f.weekly) : null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] });
      toast('Лимиты платежей сохранены');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Лимиты платежей"
      description="Лимиты — на всю компанию (ФН-02). Календарь выделяет дни и недели, где оплаченное, заявки и плановые платежи по договорам вместе выше лимита. Заявка на оплату, с которой переданные, остановленные и оплаченные платежи дня или недели выше лимита, при передаче ждёт решения финансового менеджера. Пусто — без лимита."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!ok(f.daily) || !ok(f.weekly)}
            onClick={() => save.mutate()}
          >
            Сохранить лимиты
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="На день, ₸" error={ok(f.daily) ? undefined : 'Сумма больше нуля'}>
          <Input
            inputMode="decimal"
            value={f.daily}
            onChange={(e) => setF({ ...f, daily: e.target.value })}
          />
        </Field>
        <Field label="На неделю, ₸" error={ok(f.weekly) ? undefined : 'Сумма больше нуля'}>
          <Input
            inputMode="decimal"
            value={f.weekly}
            onChange={(e) => setF({ ...f, weekly: e.target.value })}
          />
        </Field>
      </div>
      <ErrorBox error={save.error} className="mt-3" />
    </Dialog>
  );
}

/* ───────────────────────── Комплекты документов к оплате ───────────────────────── */

const RULES_KEY = ['finance', 'doc-rules'];

function DocRulesView() {
  const q = useQuery({
    queryKey: RULES_KEY,
    queryFn: () => api<PaymentDocRulesResponse>('/finance/payment-doc-rules'),
  });
  const [kind, setKind] = useState<DocRuleContractKind>('any');
  const qc = useQueryClient();
  const fileRule = useMutation({
    mutationFn: (requireFile: boolean) =>
      api<PaymentDocRulesResponse>('/finance/payment-doc-settings', { method: 'PUT', body: { requireFile } }),
    onSuccess: (x) => {
      qc.setQueryData(RULES_KEY, x);
      qc.invalidateQueries({ queryKey: ['procurement'] });
      toast(x.requireFile ? 'Документ считается полученным с файлом' : 'Документ можно отметить без файла');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const { rules, can } = q.data;
  const own = rules.filter((r) => r.contractKind === kind);
  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-3xl text-[14px] text-ink-2">
        Какие документы нужны для оплаты — по виду договора и типу платежа. Если для вида договора свой
        комплект не задан, действует комплект «Любой договор». При передаче на оплату состав комплекта
        фиксируется в заявке, правка справочника его уже не меняет. Справочник ведут финансовый менеджер и
        администратор.
      </p>
      {can.edit ? (
        <Checkbox
          label="Документ считается полученным, только когда к нему приложен файл"
          description="Без этого достаточно отметки сотрудника, который готовит заявку на оплату. Действует для заявок, ещё не переданных на оплату."
          checked={q.data.requireFile}
          disabled={fileRule.isPending}
          onChange={(e) => fileRule.mutate(e.target.checked)}
        />
      ) : (
        <p className="text-[14px] text-ink-2">
          {q.data.requireFile
            ? 'Документ считается полученным, когда к нему приложен файл.'
            : 'Документ отмечает сотрудник, который готовит заявку на оплату; файл прикладывается к документу.'}
        </p>
      )}
      <Field label="Вид договора" className="max-w-sm">
        <Select value={kind} onChange={(e) => setKind(e.target.value as DocRuleContractKind)}>
          {(Object.keys(DOC_RULE_CONTRACT_KINDS) as DocRuleContractKind[]).map((k) => (
            <option key={k} value={k}>
              {DOC_RULE_CONTRACT_KINDS[k]}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid gap-4 lg:grid-cols-2">
        {(Object.keys(PAYMENT_KINDS) as PaymentKind[]).map((pk) => (
          <RuleSet
            key={pk}
            contractKind={kind}
            paymentKind={pk}
            rules={own.filter((r) => r.paymentKind === pk)}
            fallback={
              kind !== 'any' && !own.some((r) => r.paymentKind === pk)
                ? rules.filter((r) => r.contractKind === 'any' && r.paymentKind === pk && r.active)
                : null
            }
            editable={can.edit}
          />
        ))}
      </div>
    </div>
  );
}

function RuleSet({
  contractKind,
  paymentKind,
  rules,
  fallback,
  editable,
}: {
  contractKind: DocRuleContractKind;
  paymentKind: PaymentKind;
  rules: PaymentDocRuleView[];
  fallback: PaymentDocRuleView[] | null;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const patch = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      api<PaymentDocRulesResponse>(`/finance/payment-doc-rules/${v.id}`, {
        method: 'PATCH',
        body: { active: v.active },
      }),
    onSuccess: (x) => qc.setQueryData(RULES_KEY, x),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Panel
      title={PAYMENT_KINDS[paymentKind]}
      meta={
        fallback
          ? `свой комплект не задан — действует «Любой договор»${editable ? '; новый документ добавится к его копии' : ''}`
          : undefined
      }
      actions={
        editable ? (
          <Button
            size="sm"
            variant="secondary"
            icon={<Plus className="size-4" />}
            onClick={() => setAdding(true)}
          >
            Документ
          </Button>
        ) : null
      }
    >
      {rules.length || fallback?.length ? (
        <ul className="flex flex-col gap-2">
          {(fallback ?? rules).map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 text-[14px]">
              <span className={clsx('min-w-0', !r.active && 'text-ink-3 line-through')}>
                {r.title}
                {r.condition ? (
                  <span className="block text-[12px] text-ink-2">{DOC_RULE_CONDITIONS[r.condition]}</span>
                ) : null}
                {fallback ? (
                  <span className="block text-[12px] text-ink-2">из комплекта «Любой договор»</span>
                ) : null}
              </span>
              {editable && !fallback ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={patch.isPending && patch.variables?.id === r.id}
                  onClick={() => patch.mutate({ id: r.id, active: !r.active })}
                >
                  {r.active ? 'Выключить' : 'Включить'}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">Документы не нужны.</p>
      )}
      {adding ? (
        <AddRuleDialog
          contractKind={contractKind}
          paymentKind={paymentKind}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </Panel>
  );
}

function AddRuleDialog({
  contractKind,
  paymentKind,
  onClose,
}: {
  contractKind: DocRuleContractKind;
  paymentKind: PaymentKind;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({ key: 'invoice', title: '', condition: '' as DocRuleCondition | '' });
  const custom = f.key === '';
  const title = custom ? f.title.trim() : PAYMENT_DOC_KINDS[f.key as keyof typeof PAYMENT_DOC_KINDS];
  const save = useMutation({
    mutationFn: () =>
      api<PaymentDocRulesResponse>('/finance/payment-doc-rules', {
        body: {
          contractKind,
          paymentKind,
          ...(custom ? {} : { key: f.key }),
          title,
          condition: f.condition || null,
        },
      }),
    onSuccess: (x) => {
      qc.setQueryData(RULES_KEY, x);
      toast('Документ добавлен в комплект');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Документ в комплект"
      description={`${DOC_RULE_CONTRACT_KINDS[contractKind]}, ${PAYMENT_KINDS[paymentKind].toLowerCase()}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!title || title.length < 2}
            onClick={() => save.mutate()}
          >
            Добавить документ
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Документ">
          <Select value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })}>
            {Object.entries(PAYMENT_DOC_KINDS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
            <option value="">Другой документ…</option>
          </Select>
        </Field>
        {custom ? (
          <Field label="Название документа">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>
        ) : null}
        <Field label="Когда нужен">
          <Select
            value={f.condition}
            onChange={(e) => setF({ ...f, condition: e.target.value as DocRuleCondition | '' })}
          >
            <option value="">всегда</option>
            {Object.entries(DOC_RULE_CONDITIONS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorBox error={save.error} />
      </div>
    </Dialog>
  );
}
